import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Download, FileText } from "lucide-react";

const KIND_CONFIG = {
  worksheet: { title: "Generate a worksheet", endpoint: "/api/tutor/worksheet",
    fields: ["topic", "difficulty", "num_questions"] },
  quiz: { title: "Generate a quiz", endpoint: "/api/tutor/quiz",
    fields: ["topic", "num_questions"] },
  test: { title: "Generate a test", endpoint: "/api/tutor/test",
    fields: ["topic", "test_type"] },
  "study-plan": { title: "Build a study plan", endpoint: "/api/tutor/study-plan",
    fields: ["goal", "exam_date", "hours_per_week"], serverKind: "study_plan" },
};

const Render = ({ kind, item }) => {
  if (!item) return null;
  if (kind === "worksheet") {
    return (
      <div data-testid="result-worksheet">
        <h3 className="font-display font-bold text-xl">{item.title}</h3>
        <p className="text-sm text-[#4B5563] mt-1">{item.instructions}</p>
        <ol className="mt-4 space-y-3 list-decimal pl-6 text-sm">
          {item.questions?.map((q, i) => (
            <li key={i}><div className="font-semibold">{q.question}</div>
              <div className="text-[#4B5563] mt-1">Answer: {q.answer}</div></li>
          ))}
        </ol>
      </div>
    );
  }
  if (kind === "quiz") {
    return (
      <div data-testid="result-quiz">
        <h3 className="font-display font-bold text-xl">{item.title}</h3>
        <ol className="mt-4 space-y-4 list-decimal pl-6 text-sm">
          {item.questions?.map((q, i) => (
            <li key={i}>
              <div className="font-semibold">[{q.type?.toUpperCase()}] {q.question}</div>
              {q.options && <ul className="mt-1 list-disc pl-5 text-[#4B5563]">
                {q.options.map((o, j) => <li key={`${o}-${j}`}>{o}</li>)}
              </ul>}
              <div className="text-[#064E3B] text-xs mt-1">{q.explanation}</div>
            </li>
          ))}
        </ol>
      </div>
    );
  }
  if (kind === "test") {
    return (
      <div data-testid="result-test">
        <h3 className="font-display font-bold text-xl">{item.title}</h3>
        <p className="text-sm text-[#4B5563]">{item.instructions} · Total marks: {item.total_marks}</p>
        {item.sections?.map((s, i) => (
          <div key={i} className="mt-4">
            <div className="font-display font-bold">{s.name} ({s.marks_each} marks each)</div>
            <ol className="mt-2 list-decimal pl-6 text-sm space-y-2">
              {s.questions?.map((q, j) => <li key={j}>{q.question}</li>)}
            </ol>
          </div>
        ))}
      </div>
    );
  }
  if (kind === "study-plan") {
    return (
      <div data-testid="result-study-plan">
        <h3 className="font-display font-bold text-xl">{item.title}</h3>
        <p className="text-sm text-[#4B5563]">{item.summary}</p>
        {item.weeks?.map((w, i) => (
          <div key={i} className="mt-4 border border-[#E5E7EB] rounded-xl p-4">
            <div className="font-display font-bold">Week {w.week}: {w.focus}</div>
            <ul className="mt-2 text-sm space-y-1">
              {w.daily?.map((d, j) => <li key={j}><b>{d.day}:</b> {d.activity} <span className="text-[#4B5563]">({d.minutes} min)</span></li>)}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export default function TutorGenerate() {
  const { kind: kindParam } = useParams();
  const loc = useLocation();
  // kind from /tutor/:kind path segment
  const kind = kindParam || loc.pathname.split("/")[2] || "worksheet";
  const nav = useNavigate();
  const cfg = KIND_CONFIG[kind];

  const [profile, setProfile] = useState(null);
  const [opts, setOpts] = useState({ grades: [], subjects: [] });
  const [form, setForm] = useState({
    grade: "", subject: "", topic: "", difficulty: "Medium",
    num_questions: 8, test_type: "unit", goal: "", exam_date: "",
    hours_per_week: 5,
  });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [resultId, setResultId] = useState(null);

  useEffect(() => {
    api.get("/api/tutor/profile").then(({ data }) => {
      if (!data.profile.onboarded) { nav("/tutor/onboarding"); return; }
      setProfile(data.profile);
      setOpts(data.options);
      setForm((f) => ({
        ...f, grade: data.profile.grade,
        subject: data.profile.subjects[0] || "Mathematics",
      }));
    }).catch(() => nav("/login"));
    // run-on-mount/kind-change: load profile + reset defaults
  }, [nav, kind]);

  if (!cfg) return <div data-testid="tutor-unknown-kind">Unknown generator.</div>;

  const submit = async () => {
    setBusy(true); setResult(null); setResultId(null);
    try {
      const payload = { grade: form.grade, subject: form.subject };
      for (const f of cfg.fields) payload[f.replace("-", "_")] = form[f.replace("-", "_")];
      const { data } = await api.post(cfg.endpoint, payload);
      const { id, ...rest } = data;
      setResultId(id); setResult(rest);
      toast.success("Done!");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to generate.";
      toast.error(msg);
      if (e?.response?.status === 402) nav("/tutor/pricing");
    } finally { setBusy(false); }
  };

  if (!profile) return <div className="p-4" data-testid="tutor-loading">Loading…</div>;

  return (
    <div data-testid={`tutor-generate-${kind}`}>
      <div className="label-eyebrow flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#D92D20]" /> Teacher4Me</div>
      <h1 className="font-display font-black text-3xl md:text-4xl mt-1">{cfg.title}</h1>

      <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6 grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-semibold">Grade</label>
          <select data-testid="gen-grade" value={form.grade}
            onChange={(e) => setForm({ ...form, grade: e.target.value })}
            className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
            {opts.grades.map((g) => <option key={g} value={g}>{g}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold">Subject</label>
          <select data-testid="gen-subject" value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
            {opts.subjects.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        {cfg.fields.includes("topic") && (
          <div className="sm:col-span-2">
            <label className="text-sm font-semibold">Topic</label>
            <input data-testid="gen-topic" value={form.topic}
              onChange={(e) => setForm({ ...form, topic: e.target.value })}
              placeholder="e.g. Fractions and decimals"
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        {cfg.fields.includes("difficulty") && (
          <div>
            <label className="text-sm font-semibold">Difficulty</label>
            <select data-testid="gen-difficulty" value={form.difficulty}
              onChange={(e) => setForm({ ...form, difficulty: e.target.value })}
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
              {["Easy", "Medium", "Hard"].map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
        )}

        {cfg.fields.includes("num_questions") && (
          <div>
            <label className="text-sm font-semibold"># of questions</label>
            <input type="number" min={4} max={20} data-testid="gen-num"
              value={form.num_questions}
              onChange={(e) => setForm({ ...form, num_questions: +e.target.value })}
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        {cfg.fields.includes("test_type") && (
          <div>
            <label className="text-sm font-semibold">Test type</label>
            <select data-testid="gen-test-type" value={form.test_type}
              onChange={(e) => setForm({ ...form, test_type: e.target.value })}
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm">
              <option value="unit">Unit</option>
              <option value="midterm">Midterm</option>
              <option value="final">Final</option>
            </select>
          </div>
        )}

        {cfg.fields.includes("goal") && (
          <div className="sm:col-span-2">
            <label className="text-sm font-semibold">Goal</label>
            <input data-testid="gen-goal" value={form.goal}
              onChange={(e) => setForm({ ...form, goal: e.target.value })}
              placeholder="e.g. Ace the algebra unit"
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        {cfg.fields.includes("exam_date") && (
          <div>
            <label className="text-sm font-semibold">Exam date</label>
            <input type="date" data-testid="gen-exam-date" value={form.exam_date}
              onChange={(e) => setForm({ ...form, exam_date: e.target.value })}
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        {cfg.fields.includes("hours_per_week") && (
          <div>
            <label className="text-sm font-semibold">Hours / week</label>
            <input type="number" min={1} max={40} data-testid="gen-hours"
              value={form.hours_per_week}
              onChange={(e) => setForm({ ...form, hours_per_week: +e.target.value })}
              className="mt-1 w-full border border-[#E5E7EB] rounded-lg px-3 py-2 text-sm" />
          </div>
        )}

        <div className="sm:col-span-2 flex items-center gap-3">
          <button data-testid="gen-submit" onClick={submit} disabled={busy}
            className="btn-primary disabled:opacity-50 inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> {busy ? "Generating…" : "Generate"}
          </button>
          {resultId && kind !== "study-plan" && (
            <>
              <a data-testid="gen-download"
                href={`${process.env.REACT_APP_BACKEND_URL}/api/tutor/items/${resultId}/pdf`}
                target="_blank" rel="noreferrer"
                onClick={(e) => {
                  // axios interceptor adds token only for in-app; for direct link, use ?token= isn't supported.
                  // We use fetch+blob below; prevent default and trigger it manually.
                  e.preventDefault();
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/items/${resultId}/pdf`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("tmo_token")}` }})
                    .then((r) => r.blob())
                    .then((b) => {
                      const url = URL.createObjectURL(b);
                      const a = document.createElement("a");
                      a.href = url; a.download = `${kind}-${resultId.slice(0,8)}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    });
                }}
                className="btn-outline inline-flex items-center gap-2 text-sm">
                <Download className="w-4 h-4" /> PDF (no answers)
              </a>
              <a data-testid="gen-download-with-answers" href="#"
                onClick={(e) => {
                  e.preventDefault();
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/tutor/items/${resultId}/pdf?with_answers=true`,
                    { headers: { Authorization: `Bearer ${localStorage.getItem("tmo_token")}` }})
                    .then((r) => r.blob())
                    .then((b) => {
                      const url = URL.createObjectURL(b);
                      const a = document.createElement("a");
                      a.href = url; a.download = `${kind}-key-${resultId.slice(0,8)}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove();
                      URL.revokeObjectURL(url);
                    });
                }}
                className="btn-outline inline-flex items-center gap-2 text-sm">
                <FileText className="w-4 h-4" /> PDF with answer key
              </a>
            </>
          )}
        </div>
      </div>

      {result && (
        <div className="mt-6 bg-white border border-[#E5E7EB] rounded-2xl p-6">
          <Render kind={kind} item={result} />
          <Link to={`/tutor/items/${resultId}`} data-testid="gen-view-saved"
            className="text-sm font-semibold text-[#D92D20] mt-4 inline-block">Open saved item →</Link>
        </div>
      )}
    </div>
  );
}
